import React, { useState } from 'react';
import { FileText, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Tooltip } from '../../../components/ui/tooltip';
import ReactApexChart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import {
  PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
  PIPEFITTER_SEND_EMAIL_PROJECT_SUMMARY,
} from '../../../redux/Pipefitter/types';
import { configurationConstants } from '../constants/configurationConstants';
import { SendReportEmailModal } from './SendReportEmailModal';
import { projectSummaryHelper, hasProjectSummaryChartData } from '../helpers/reportHelpers';

interface ProjectSummaryProps {
  isLoading: boolean;
  projectSummaryData: any;
  projectNumber: string;
  canReport: boolean;
}

export const ProjectSummary: React.FC<ProjectSummaryProps> = ({
  isLoading,
  projectSummaryData,
  projectNumber,
  canReport,
}) => {
  const dispatch = useDispatch();
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const project = useSelector((state: any) => state.project?.selectedProject);
  const sequenceStatus = project?.sequenceGenerationStatus;
  const randomSequenceStatus = project?.randomSequenceGenerationStatus;

  const enableDownloadOrSendEmailButton = () => {
    return (
      sequenceStatus === configurationConstants.sequenceGenerationStatus.Completed &&
      randomSequenceStatus === configurationConstants.sequenceGenerationStatus.Completed
    );
  };

  const hasValidData = hasProjectSummaryChartData(projectSummaryData);

  const handleGeneratePDF = async () => {
    try {
      setIsPdfDownloading(true);
      dispatch({
        type: PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
        payload: { projectNumber },
        cb: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${projectNumber}_Project-Summary-Report.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setIsPdfDownloading(false);
        },
      });
    } catch (error) {
      setIsPdfDownloading(false);
      console.error('Error generating PDF:', error);
    }
  };

  const confirmSendEmail = async () => {
    setShowEmailModal(true);
  };

  const chartData = projectSummaryHelper(projectSummaryData);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500">Loading project summary...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {hasValidData && canReport && (
              <div className="flex justify-end gap-2">
                <Tooltip content={isPdfDownloading ? 'Downloading...' : 'Download PDF'}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleGeneratePDF}
                    disabled={!enableDownloadOrSendEmailButton() || isPdfDownloading}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                </Tooltip>
                <Tooltip content={isSendingEmail ? 'Sending email...' : 'Send Email'}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={confirmSendEmail}
                    disabled={!enableDownloadOrSendEmailButton() || isSendingEmail}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </div>
            )}
            {!enableDownloadOrSendEmailButton() || !hasValidData ? (
              <div className="text-center py-8 text-gray-500">No data available</div>
            ) : (
              <div id="chart">
                <ReactApexChart
                  options={chartData.options}
                  series={chartData.series}
                  type="bar"
                  height={290}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <SendReportEmailModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        projectNumber={projectNumber}
        title="Send Project Summary Report"
        isSending={isSendingEmail}
        onSend={(emails) => {
          setIsSendingEmail(true);
          dispatch({
            type: PIPEFITTER_SEND_EMAIL_PROJECT_SUMMARY,
            payload: { projectNumber, emails },
            cb: () => {
              setShowEmailModal(false);
              setIsSendingEmail(false);
            },
            error: () => setIsSendingEmail(false),
          });
        }}
      />
    </>
  );
};
